import React, { useState, useEffect } from 'react';
import './Pantry.css';

interface PantryItem {
  id: string;
  name: string;
  category: string;
  quantity: number;
  unit: string;
  expiryDate?: string;
  addedDate: string;
}

const Pantry: React.FC = () => {
  const [items, setItems] = useState<PantryItem[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [addMethod, setAddMethod] = useState<'manual' | 'barcode' | 'photo'>('manual');

  const categories = [
    'All',
    'Grains & Pasta',
    'Proteins',
    'Dairy',
    'Vegetables',
    'Fruits',
    'Condiments',
    'Spices',
    'Beverages',
    'Snacks',
    'Other'
  ];

  useEffect(() => {
    // TODO: Fetch pantry items from API
    loadPantryItems();
  }, []);

  const loadPantryItems = async () => {
    // Placeholder for API call
    setItems([]);
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleAddItem = async (newItem: Omit<PantryItem, 'id' | 'addedDate'>) => {
    // TODO: API call to add item
    const item: PantryItem = {
      ...newItem,
      id: Date.now().toString(),
      addedDate: new Date().toISOString(),
    };
    setItems([...items, item]);
    setShowAddModal(false);
  };

  const handleDeleteItem = async (id: string) => {
    // TODO: API call to delete item
    setItems(items.filter(item => item.id !== id));
  };

  const getExpiryStatus = (expiryDate?: string) => {
    if (!expiryDate) return 'normal';
    const days = Math.ceil((new Date(expiryDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    if (days < 0) return 'expired';
    if (days <= 3) return 'expiring-soon';
    if (days <= 7) return 'expiring-week';
    return 'normal';
  };

  return (
    <div className="pantry-page">
      <div className="page-header">
        <h1 className="page-title">Pantry Management</h1>
        <p className="page-subtitle">Track your ingredients and manage inventory</p>
      </div>

      {/* Actions Bar */}
      <div className="actions-bar">
        <div className="search-filter">
          <input
            type="text"
            placeholder="Search items..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="category-filter"
          >
            {categories.map(cat => (
              <option key={cat} value={cat.toLowerCase().replace(' & ', '-')}>
                {cat}
              </option>
            ))}
          </select>
        </div>
        <button 
          className="button-primary"
          onClick={() => setShowAddModal(true)}
        >
          + Add Items
        </button>
      </div>

      {/* Empty State */}
      {items.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">🥫</div>
          <h3>Your pantry is empty</h3>
          <p>Start by adding items to track your inventory</p>
          <div className="add-methods">
            <button 
              className="method-card"
              onClick={() => {
                setAddMethod('manual');
                setShowAddModal(true);
              }}
            >
              <div className="method-icon">✏️</div>
              <div className="method-title">Manual Entry</div>
              <div className="method-desc">Type in item details</div>
            </button>
            <button 
              className="method-card"
              onClick={() => {
                setAddMethod('barcode');
                setShowAddModal(true);
              }}
            >
              <div className="method-icon">📷</div>
              <div className="method-title">Scan Barcode</div>
              <div className="method-desc">Use camera to scan</div>
            </button>
            <button 
              className="method-card"
              onClick={() => {
                setAddMethod('photo');
                setShowAddModal(true);
              }}
            >
              <div className="method-icon">🖼️</div>
              <div className="method-title">Take Photo</div>
              <div className="method-desc">AI recognizes items</div>
            </button>
          </div>
        </div>
      )}

      {/* Items Grid */}
      {items.length > 0 && (
        <div className="pantry-grid">
          {filteredItems.map(item => (
            <div key={item.id} className={`pantry-item ${getExpiryStatus(item.expiryDate)}`}>
              <div className="item-header">
                <h3 className="item-name">{item.name}</h3>
                <button 
                  className="delete-btn"
                  onClick={() => handleDeleteItem(item.id)}
                >
                  ✕
                </button>
              </div>
              <div className="item-details">
                <div className="detail-row">
                  <span className="detail-label">Category:</span>
                  <span className="detail-value">{item.category}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Quantity:</span>
                  <span className="detail-value">{item.quantity} {item.unit}</span>
                </div>
                {item.expiryDate && (
                  <div className="detail-row">
                    <span className="detail-label">Expires:</span>
                    <span className="detail-value">
                      {new Date(item.expiryDate).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
              {getExpiryStatus(item.expiryDate) === 'expiring-soon' && (
                <div className="expiry-warning">⚠️ Expiring soon!</div>
              )}
              {getExpiryStatus(item.expiryDate) === 'expired' && (
                <div className="expiry-warning expired">❌ Expired</div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add Item Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Pantry Items</h2>
              <button 
                className="close-btn"
                onClick={() => setShowAddModal(false)}
              >
                ✕
              </button>
            </div>
            
            <div className="add-method-tabs">
              <button 
                className={`tab ${addMethod === 'manual' ? 'active' : ''}`}
                onClick={() => setAddMethod('manual')}
              >
                Manual Entry
              </button>
              <button 
                className={`tab ${addMethod === 'barcode' ? 'active' : ''}`}
                onClick={() => setAddMethod('barcode')}
              >
                Scan Barcode
              </button>
              <button 
                className={`tab ${addMethod === 'photo' ? 'active' : ''}`}
                onClick={() => setAddMethod('photo')}
              >
                Take Photo
              </button>
            </div>

            {addMethod === 'manual' && (
              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                handleAddItem({
                  name: formData.get('name') as string,
                  category: formData.get('category') as string,
                  quantity: Number(formData.get('quantity')),
                  unit: formData.get('unit') as string,
                  expiryDate: formData.get('expiryDate') as string || undefined,
                });
              }}>
                <div className="form-group">
                  <label>Item Name *</label>
                  <input type="text" name="name" required />
                </div>
                <div className="form-group">
                  <label>Category *</label>
                  <select name="category" required>
                    <option value="">Select category</option>
                    {categories.slice(1).map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Quantity *</label>
                    <input type="number" name="quantity" min="0" step="0.1" required />
                  </div>
                  <div className="form-group">
                    <label>Unit *</label>
                    <select name="unit" required>
                      <option value="units">Units</option>
                      <option value="oz">Ounces</option>
                      <option value="lb">Pounds</option>
                      <option value="g">Grams</option>
                      <option value="kg">Kilograms</option>
                      <option value="ml">Milliliters</option>
                      <option value="l">Liters</option>
                      <option value="cups">Cups</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>Expiry Date (optional)</label>
                  <input type="date" name="expiryDate" />
                </div>
                <div className="modal-actions">
                  <button type="button" className="button-secondary" onClick={() => setShowAddModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="button-primary">
                    Add Item
                  </button>
                </div>
              </form>
            )}

            {addMethod === 'barcode' && (
              <div className="feature-placeholder">
                <div className="placeholder-icon">📷</div>
                <h3>Barcode Scanner</h3>
                <p>Camera-based barcode scanning will be available soon.</p>
                <p>This feature will allow you to quickly add items by scanning their barcodes.</p>
              </div>
            )}

            {addMethod === 'photo' && (
              <div className="feature-placeholder">
                <div className="placeholder-icon">🤖</div>
                <h3>AI Photo Recognition</h3>
                <p>AWS Rekognition integration coming soon.</p>
                <p>Take a photo of your pantry or fridge, and AI will identify the items automatically.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Pantry;
